import React, { useState } from "react";
import { Mail, Phone, MapPin, Clock, Download, MessageCircle, Send } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Reveal } from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import SEO from "../components/SEO";
import { CONTACT } from "../data/content";

export default function Contact() {
  const { tr, lang, isAr } = useLang();
  const [form, setForm] = useState({ name: "", company: "", message: "" });

  const composed = () => {
    const lines = [
      `${tr.contact.name}: ${form.name || "-"}`,
      `${tr.contact.company}: ${form.company || "-"}`,
      `${tr.contact.message}: ${form.message || "-"}`,
    ];
    return lines.join("\n");
  };

  const sendWhatsApp = (e) => {
    e.preventDefault();
    const text = encodeURIComponent(`ICON Trading Co. — Inquiry\n\n${composed()}`);
    window.open(`https://wa.me/${CONTACT.whatsapp}?text=${text}`, "_blank");
  };

  const sendEmail = () => {
    const subject = encodeURIComponent("ICON Trading Co. — Inquiry");
    const body = encodeURIComponent(composed());
    window.location.href = `mailto:${CONTACT.email}?subject=${subject}&body=${body}`;
  };

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const details = [
    { icon: Mail, label: tr.contact.emailLabel, value: CONTACT.emailPrimary, href: `mailto:${CONTACT.emailPrimary}`, ltr: true },
    { icon: Mail, label: tr.contact.emailLabel, value: CONTACT.email, href: `mailto:${CONTACT.email}`, ltr: true },
    { icon: Phone, label: tr.contact.phoneLabel, value: CONTACT.phones[0], href: `tel:${CONTACT.phones[0].replace(/\s/g, "")}`, ltr: true },
    { icon: Phone, label: tr.contact.additionalLabel, value: CONTACT.phones.slice(1).join(" · "), href: `tel:${CONTACT.phones[1].replace(/\s/g, "")}`, ltr: true },
    { icon: MapPin, label: tr.contact.addressLabel, value: isAr ? CONTACT.addressAr : CONTACT.addressEn },
  ];

  return (
    <div data-testid="contact-page" className="pt-28 sm:pt-36">
      <SEO page="contact" lang={lang} />

      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading overline={tr.contact.badge} title={tr.contact.title} desc={tr.contact.desc} />
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20">
        <div className="grid gap-8 lg:grid-cols-12">
          {/* Form */}
          <Reveal variant="left" className="lg:col-span-7">
            <div className="rounded-[2rem] border border-border bg-white p-7 sm:p-10 shadow-xl shadow-brand-blue/5">
              <h3 className="text-xl sm:text-2xl font-800 text-ink">{tr.contact.formTitle}</h3>
              <form onSubmit={sendWhatsApp} className="mt-7 space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-600 text-slate">{tr.contact.name}</label>
                    <input
                      data-testid="contact-name"
                      value={form.name}
                      onChange={onChange("name")}
                      required
                      className="mt-2 w-full rounded-xl border border-border bg-surface px-4 py-3 text-sm text-ink outline-none transition-all focus:border-brand-blue focus:bg-white focus:ring-2 focus:ring-brand-blue/15"
                      placeholder={tr.contact.name}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-600 text-slate">{tr.contact.company}</label>
                    <input
                      data-testid="contact-company"
                      value={form.company}
                      onChange={onChange("company")}
                      className="mt-2 w-full rounded-xl border border-border bg-surface px-4 py-3 text-sm text-ink outline-none transition-all focus:border-brand-blue focus:bg-white focus:ring-2 focus:ring-brand-blue/15"
                      placeholder={tr.contact.company}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-600 text-slate">{tr.contact.message}</label>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={onChange("message")}
                    required
                    rows={5}
                    className="mt-2 w-full resize-none rounded-xl border border-border bg-surface px-4 py-3 text-sm text-ink outline-none transition-all focus:border-brand-blue focus:bg-white focus:ring-2 focus:ring-brand-blue/15"
                    placeholder={tr.contact.message}
                  />
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="submit"
                    data-testid="contact-whatsapp-btn"
                    className="group flex flex-1 items-center justify-center gap-2 rounded-full bg-brand-blue px-6 py-3.5 text-sm font-600 text-white transition-all hover:bg-brand-blue-dark"
                  >
                    <MessageCircle className="h-4 w-4" /> {tr.contact.send}
                  </button>
                  <button
                    type="button"
                    onClick={sendEmail}
                    data-testid="contact-email-btn"
                    className="group flex flex-1 items-center justify-center gap-2 rounded-full border border-ink/15 px-6 py-3.5 text-sm font-600 text-ink transition-all hover:border-brand-red hover:text-brand-red"
                  >
                    <Send className="h-4 w-4" /> {tr.contact.sendEmail}
                  </button>
                </div>
              </form>
            </div>
          </Reveal>

          {/* Details + download */}
          <Reveal variant="right" className="lg:col-span-5">
            <div className="flex h-full flex-col gap-5">
              <div className="grid gap-px overflow-hidden rounded-[2rem] border border-border bg-border">
                {details.map((d) => (
                  <div key={d.value} className="flex items-start gap-4 bg-white p-6">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-blue/10 text-brand-blue">
                      <d.icon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-700 uppercase tracking-wide text-slate/70">{d.label}</p>
                      {d.href ? (
                        <a href={d.href} dir={d.ltr ? "ltr" : undefined} className="mt-1 block break-words text-sm font-600 text-ink hover:text-brand-blue">{d.value}</a>
                      ) : (
                        <p className="mt-1 text-sm font-600 text-ink">{d.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Download catalogue */}
              <div className="relative overflow-hidden rounded-[2rem] bg-ink p-8 text-white">
                <div className="absolute -bottom-16 -right-16 h-48 w-48 rounded-full bg-brand-red/30 blur-3xl" />
                <p className="relative text-lg font-700">CATALOGUE 01</p>
                <p className="relative mt-1 text-sm text-white/60">{tr.footer.tagline}</p>
                <a
                  href={CONTACT.catalogue}
                  download
                  data-testid="download-catalogue-btn"
                  className="relative mt-5 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-700 text-ink transition-all hover:bg-brand-blue hover:text-white"
                >
                  <Download className="h-4 w-4" /> {tr.contact.download}
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Map */}
        <Reveal variant="up" className="mt-8">
          <div className="overflow-hidden rounded-[2rem] border border-border">
            <iframe
              title="ICON Trading Co. location"
              data-testid="contact-map"
              src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapsQuery)}&output=embed`}
              className="h-[360px] w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
